import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';
import { useDicomUploadLifecycle } from '@/views/case-settings/upload/shared/useDicomUploadLifecycle';
import { createXRayStudy, type XRayFile } from '@/views/case-settings/upload/xray/createXRayStudy';
import { useCaseSettings } from '@/stores/caseSettings/store';
import type { CaseStudy } from '@/stores/caseSettings/types';

export const useXrayUploadViewModel = defineStore('xray-upload-view', () => {
    const caseSettings = useCaseSettings();

    const lifecycle = useDicomUploadLifecycle<XRayFile>({
        modality: 'xray',
        requireSingleSeries: false,
        uploadType: 'xray-2d-3d',
        extractFile: (xf) => xf.file,
        createStudy: async ({ caseId, inputs }) => createXRayStudy(caseId, inputs),
        buildWorkerPayload: ({ inputs }) => inputs.map((xf) => ({ file: xf.file, view: xf.view })),
        onUploadComplete: ({ inputs }) => {
            caseSettings.state.case.study = {
                name: 'X-ray upload',
                series: 'xray',
                fileCount: inputs.length,
                sourceModality: 'xray',
            };
        },
        messages: {
            autoUpload: 'X-rays auto-uploaded',
            userConfirmed: 'X-rays uploaded by user',
            uploadDespiteMismatch: 'X-rays uploaded despite patient data mismatch',
        },
    });

    // VM-owned file slots (arrays to match v-file-upload v-model shape)
    const apFile = ref<File[]>([]);
    const leftObliqueFile = ref<File[]>([]);
    const rightObliqueFile = ref<File[]>([]);

    const lastUploaded = reactive({
        ap: null as File | null,
        leftOblique: null as File | null,
        rightOblique: null as File | null,
    });

    const selectedAllFiles = computed(() =>
        apFile.value.length > 0 && leftObliqueFile.value.length > 0 && rightObliqueFile.value.length > 0,
    );

    const hasFilesChanged = computed(() => {
        if (!lastUploaded.ap && !lastUploaded.leftOblique && !lastUploaded.rightOblique) return true;
        return (
            apFile.value[0]?.name !== lastUploaded.ap?.name ||
            leftObliqueFile.value[0]?.name !== lastUploaded.leftOblique?.name ||
            rightObliqueFile.value[0]?.name !== lastUploaded.rightOblique?.name
        );
    });

    const canStartProcessing = computed(() =>
        selectedAllFiles.value && (!lifecycle.hasUploaded.value || hasFilesChanged.value),
    );

    const getStudy = computed((): CaseStudy | null => caseSettings.case.study);
    const hasScan = computed(() => lifecycle.hasUploaded.value || !!getStudy.value);
    const hasEnteredPatientData = computed(() => caseSettings.hasEnteredPatientData);
    const isCaseDeemedUnsuitable = computed(() => caseSettings.isCaseDeemedUnsuitable);

    async function uploadAndStartProcessing() {
        if (!selectedAllFiles.value) throw new Error('Not all files are selected');
        if (caseSettings.case.id == null) throw new Error('Case-id is null on upload');

        await lifecycle.start([
            { view: 'ap', file: apFile.value[0] },
            { view: 'left-oblique', file: leftObliqueFile.value[0] },
            { view: 'right-oblique', file: rightObliqueFile.value[0] },
        ]);

        lastUploaded.ap = apFile.value[0] ?? null;
        lastUploaded.leftOblique = leftObliqueFile.value[0] ?? null;
        lastUploaded.rightOblique = rightObliqueFile.value[0] ?? null;
    }

    return {
        // lifecycle passthrough
        state: lifecycle.state,
        error: lifecycle.error,
        hasUploaded: lifecycle.hasUploaded,
        progress: lifecycle.progress,
        patientIdentityCheck: lifecycle.patientIdentityCheck,
        patientComparison: lifecycle.patientComparison,
        processDicomsResult: lifecycle.processDicomsResult,
        isUploading: lifecycle.isUploading,
        isWaitingForConfirmation: lifecycle.isWaitingForConfirmation,
        isError: lifecycle.isError,
        isModalError: lifecycle.isModalError,
        isPatientDicomMismatch: lifecycle.isPatientDicomMismatch,
        hasMultiplePatientIdentities: lifecycle.hasMultiplePatientIdentities,
        patient: lifecycle.patient,
        uploadAnyway: lifecycle.uploadAnyway,
        userConfirmationUpload: lifecycle.userConfirmationUpload,
        cancel: lifecycle.cancel,
        $reset: lifecycle.reset,
        xrayFiles: lifecycle.inputs,
        // VM-owned
        apFile,
        leftObliqueFile,
        rightObliqueFile,
        selectedAllFiles,
        canStartProcessing,
        hasFilesChanged,
        getStudy,
        hasScan,
        hasEnteredPatientData,
        isCaseDeemedUnsuitable,
        uploadAndStartProcessing,
    };
});
